import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { AppContext } from '../../App';
import useGlynt from './useGlynt';
import useTrainingSetSearchParams from './useTrainingSetSearchParams';
import { localizeDate } from '../../utils/localizeDate';

type ParamTypes = {
  limit: number;
  defaultPage?: number;
  labelContains?: string;
  createdAt?: string;
  updatedAt?: string;
  trainingSetId?: string;
  trained?: string;
  certified?: string;
  document?: string;
  field?: string;
  trainingRevision?: string;
  vendorId?: string;
  vendorName?: string;
};

export default function usePaginatedTrainingSets({
  limit,
  defaultPage = 0,
  labelContains,
  createdAt = '',
  updatedAt = '',
  trainingSetId,
  trained = '',
  certified = '',
  document = '',
  field = '',
  trainingRevision = '',
  vendorId = '',
  vendorName = '',
}: ParamTypes) {
  const [page, setPage] = useState(defaultPage);
  const glynt = useGlynt();
  const { dataPoolId } = useContext(AppContext);

  const { generateSearchParams } = useTrainingSetSearchParams();

  let localCreatedAtStartUTCString: any;
  let localCreatedAtEndUTCString: any;

  let localUpdatedAtStartUTCString: any;
  let localUpdatedAtEndUTCString: any;

  const searchParamsToUse = generateSearchParams(dataPoolId);

  if (createdAt !== '') {
    const dateResponse = localizeDate(createdAt);

    if (dateResponse?.start && dateResponse?.end) {
      localCreatedAtStartUTCString = dateResponse.start;
      localCreatedAtEndUTCString = dateResponse.end;
    }
  }

  if (updatedAt !== '') {
    const dateResponse = localizeDate(updatedAt);

    if (dateResponse?.start && dateResponse?.end) {
      localUpdatedAtStartUTCString = dateResponse.start;
      localUpdatedAtEndUTCString = dateResponse.end;
    }
  }

  const { data, isError, isPending, isFetching, refetch } = useQuery({
    queryKey: ['trainingSets', searchParamsToUse],

    queryFn: async () =>
      (await glynt).trainingSet.list({
        advanced: true,
        ordering: 'label',
        limit,
        offset: searchParamsToUse.page * limit,

        // optional params
        ...(labelContains !== '' && { ilabel__contains: labelContains }),
        ...(createdAt !== '' && { created_after: localCreatedAtStartUTCString }),
        ...(createdAt !== '' && { created_before: localCreatedAtEndUTCString }),
        ...(updatedAt !== '' && { updated_after: localUpdatedAtStartUTCString }),
        ...(updatedAt !== '' && { updated_before: localUpdatedAtEndUTCString }),
        ...(trainingSetId !== '' && { id: trainingSetId }),
        ...(trained !== '' && { trained: trained === 'yes' }),
        ...(certified !== '' && { certified: certified === 'yes' }),
        ...(document !== '' && { document }),
        ...(field !== '' && { field }),
        ...(trainingRevision !== '' && { training_revision: trainingRevision }),
        ...(vendorId !== '' && { vendor_id: vendorId }),
        ...(vendorName !== '' && { vendor_name: vendorName }),
      }),

    placeholderData: keepPreviousData
  });

  const total = data ? Math.ceil(data.count / limit) : 1;

  const nextPage = () => {
    if (page < total - 1) setPage((page) => page + 1);
  };

  const previousPage = () => {
    if (page > 0) setPage((page) => page - 1);
  };

  return {
    data,
    isError,
    isPending,
    isLoading: isPending,
    nextPage,
    previousPage,
    total,
    page,
    setPage,
    isFetching,
    refetch,
  };
}
